import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, Subject, tap } from 'rxjs';
import { environment } from '../../environments/environment';

export type FriendRequestStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'BLOCKED';

export type FriendshipStatus =
  | 'NONE'
  | 'PENDING_SENT'
  | 'PENDING_RECEIVED'
  | 'FRIENDS'
  | 'BLOCKED';

export interface IFriendship {
  status: FriendshipStatus;
  requestId?: string;
}

export interface IProfileImageSnippet {
  urlSmall?:  string;
  urlMedium?: string;
}

export interface IUserSnippet {
  _id:          string;
  displayName:  string;
  userName:     string;
  profileImage: IProfileImageSnippet | null;
}

/** A mutual friend snippet returned alongside each friend request */
export interface IMutualFriendSnippet {
  _id:          string;
  displayName:  string;
  userName:     string;
  profileImage: IProfileImageSnippet | null;
}

export interface IFriendRequest {
  _id:       string;
  status:    FriendRequestStatus;
  createdAt: string;
  sender:    IUserSnippet;
  recipient: IUserSnippet;
}

/**
 * Friend request enriched with:
 *  - mutualFriendCount  — total number of mutual friends
 *  - mutualFriends      — full user snippets for all mutuals (used by popup)
 */
export interface IFriendRequestWithMutuals extends IFriendRequest {
  mutualFriendCount: number;
  mutualFriends:     IMutualFriendSnippet[];
}

export interface IFriend {
  _id:          string;
  displayName:  string;
  userName:     string;
  profileImage: IProfileImageSnippet | null;
}

interface IFriendRequestResponse {
  success: boolean;
  data:    IFriendRequest;
  message: string;
}

interface IIncomingRequestsResponse {
  success: boolean;
  data:    IFriendRequestWithMutuals[];
  message: string;
}

interface IFriendRequestListResponse {
  success: boolean;
  data:    IFriendRequest[];
  message: string;
}

interface IFriendListResponse {
  success: boolean;
  data:    IFriend[];
  message: string;
}

export interface IFriendListItem extends IFriend {
  friendshipStatus:    FriendshipStatus;
  friendshipRequestId?: string;
}

interface IPaginatedFriendsResponse {
  success: boolean;
  data: {
    items: IFriendListItem[];
    total: number;
    page:  number;
    limit: number;
  };
  message: string;
}

interface IFriendshipStatusResponse {
  success: boolean;
  data:    IFriendship;
  message: string;
}

interface IMutualFriendsResponse {
  success: boolean;
  data:    IMutualFriendSnippet[];
  message: string;
}

export interface IBlockedUserItem extends IFriend {}

interface IPaginatedBlockedUsersResponse {
  success: boolean;
  data: {
    items: IBlockedUserItem[];
    total: number;
    page:  number;
    limit: number;
  };
  message: string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export interface FriendshipChange {
  /** The other person, when the action knows it (send / unfriend). */
  userId?: string;
  /** The friend request involved (cancel / accept / decline match on this). */
  requestId?: string | null;
  status: 'NONE' | 'PENDING_SENT' | 'FRIENDS';
}

@Injectable({ providedIn: 'root' })
export class FriendRequestService {

  private http       = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private baseUrl    = `${environment.apiUrl}/friend-requests`;

  private refreshEvents$ = new Subject<void>();
  onRefreshEvents$       = this.refreshEvents$.asObservable();

  /**
   * Fires after any friend action succeeds, wherever it was made, so every
   * place showing that person's friend button (profile sidebar, post cards…)
   * can update without a page reload (SAF-841). Identified by `userId` when
   * the action knows it, otherwise by the friend request's `requestId`.
   */
  private friendshipChangedSubject = new Subject<FriendshipChange>();
  friendshipChanged$ = this.friendshipChangedSubject.asObservable();

  send(recipientId: string): Observable<IFriendRequestResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY as any;
    return this.http.post<IFriendRequestResponse>(`${this.baseUrl}/${recipientId}/send`, {}).pipe(
      tap((res: any) => this.friendshipChangedSubject.next({
        userId: recipientId, status: 'PENDING_SENT', requestId: res?.data?._id ?? null,
      })),
    );
  }

  /** `userId` (the other person) is optional but lets every view showing
   *  them update by person, not just by request id. Same for decline/cancel. */
  accept(requestId: string, userId?: string): Observable<IFriendRequestResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY as any;
    return this.http.patch<IFriendRequestResponse>(`${this.baseUrl}/${requestId}/accept`, {}).pipe(
      tap(() => this.friendshipChangedSubject.next({ userId, requestId, status: 'FRIENDS' })),
    );
  }

  decline(requestId: string, userId?: string): Observable<IFriendRequestResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY as any;
    return this.http.patch<IFriendRequestResponse>(`${this.baseUrl}/${requestId}/decline`, {}).pipe(
      tap(() => this.friendshipChangedSubject.next({ userId, requestId, status: 'NONE' })),
    );
  }

  /** Declines the request (if still pending) AND blocks the sender in one call. */
  declineAndBlock(requestId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch<{ success: boolean; message: string }>(`${this.baseUrl}/${requestId}/decline-block`, {});
  }

  cancel(requestId: string, userId?: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.delete<{ success: boolean; message: string }>(`${this.baseUrl}/${requestId}/cancel`).pipe(
      tap(() => this.friendshipChangedSubject.next({ userId, requestId, status: 'NONE' })),
    );
  }

  unfriend(friendId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.delete<{ success: boolean; message: string }>(`${this.baseUrl}/${friendId}/unfriend`).pipe(
      tap(() => this.friendshipChangedSubject.next({ userId: friendId, status: 'NONE', requestId: null })),
    );
  }

  getStatus(userId: string): Observable<IFriendshipStatusResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY as any;
    return this.http.get<IFriendshipStatusResponse>(`${this.baseUrl}/${userId}/status`);
  }

  /** Returns incoming requests enriched with mutualFriendCount and mutualFriends list */
  getIncoming(): Observable<IIncomingRequestsResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY as any;
    return this.http.get<IIncomingRequestsResponse>(`${this.baseUrl}/incoming`);
  }

  getSent(): Observable<IFriendRequestListResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY as any;
    return this.http.get<IFriendRequestListResponse>(`${this.baseUrl}/sent`);
  }

  getFriends(): Observable<IFriendListResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY as any;
    return this.http.get<IFriendListResponse>(`${this.baseUrl}/friends`);
  }

  /** Paginated friends list for any profile — powers the profile page's
   *  "Friends" tab. Each item is annotated with the *viewer's own*
   *  friendship status toward that person (NONE/PENDING/FRIENDS/etc.) so
   *  the UI can render the right action button per row. */
  getFriendsByUserName(userName: string, page = 1, limit = 24): Observable<IPaginatedFriendsResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY as any;
    return this.http.get<IPaginatedFriendsResponse>(
      `${this.baseUrl}/${userName}/friends`,
      { params: { page: page.toString(), limit: limit.toString() } },
    );
  }

  /**
   * Fetches the full mutual-friends list for a given user.
   * Called lazily when the user clicks "+ N others" in the popup.
   */
  getMutualFriends(userId: string): Observable<IMutualFriendsResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY as any;
    return this.http.get<IMutualFriendsResponse>(`${this.baseUrl}/${userId}/mutual-friends`);
  }

  block(userId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.post<{ success: boolean; message: string }>(`${this.baseUrl}/${userId}/block`, {});
  }

  unblock(userId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.delete<{ success: boolean; message: string }>(`${this.baseUrl}/${userId}/unblock`);
  }

  /**
   * Paginated list of users the CURRENT user has blocked. Private by design —
   * always scoped server-side to the authenticated user (req.user.id), so
   * there's no way to fetch anyone else's blocked list.
   */
  getBlockedUsers(page = 1, limit = 24): Observable<IPaginatedBlockedUsersResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY as any;
    return this.http.get<IPaginatedBlockedUsersResponse>(
      `${this.baseUrl}/blocked`,
      { params: { page: page.toString(), limit: limit.toString() } },
    );
  }

  triggerRefresh(): void {
    this.refreshEvents$.next();
  }

  /**
   * Reads the "wait N hours" cooldown out of a 429 error from the send() endpoint
   * (e.g. "Please wait 24 hours before sending another request"). Returns null for
   * any other error so callers can fall back to their normal error handling.
   */
  parseCooldownError(err: any): number | null {
    const status = err?.status ?? err?.error?.statusCode;
    if (status !== 429) return null;
    const message: string = err?.error?.message ?? '';
    const match = message.match(/(\d+)\s*hours?/i);
    return match ? Number(match[1]) : null;
  }
}